import React from 'react';
import { UX2, constants } from '@wsb/guac-widget-core';
import { uniqueId } from 'lodash';
import { SEARCH_INPUT_LEFT_PADDING, MIN_TARGET } from '../../../../../constants';
import { getMobileRenderContainer } from '../../../../../utils/elements';
import { DESKTOP_NAV_COVER } from '../../constants/searchFormLocations';
import SearchForm from './SearchForm';
import eventListenerPolyfill from '../common/eventListenerPolyfill';
import {
  OlsConfigStore,
  RouteHandler,
  ShopViewConstants,
  ScrollWidgetActions
} from '@wsb/guac-widget-shared/lib/common/ols-core/core-bundle';
import SearchPropTypes from './ComponentPropTypes';

const alignCenter = {
  display: 'flex',
  alignItems: 'center'
};

class Search extends React.Component {
  constructor(props) {
    super(props);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.searchOpen = this.searchOpen.bind(this);
    this.searchClose = this.searchClose.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleDomSideEffect = this.handleDomSideEffect.bind(this);
    this._anchorId = uniqueId('Search-Anchor');
    this._uniqueId = uniqueId('Search');
    this._closeId = uniqueId('Close');
    this.searchWidth = props.searchWidth;

    this.state = {
      searchQuery: '',
      showSearch: false
    };
  }

  componentDidMount() {
    this.mounted = true;
    eventListenerPolyfill();
    this.prepareForIE11();
    this.calculateSearchWidth();
    this.calculateScreenWidth();
    this.calculateNavWidth();
    this.calculateSearchPos();
    window.addEventListener('resize', this.handleResize, false);
    if (this.viewTablet) {
      this.viewTablet.addEventListener('scroll', this.handleResize, { passive: true });
    }
  }

  componentWillUnmount() {
    this.mounted = false;
    window.removeEventListener('resize', this.handleResize, false);
    if (this.viewTablet) {
      this.viewTablet.removeEventListener('scroll', this.handleResize, { passive: true });
    }
  }

  componentDidUpdate() {
    const { renderMode } = this.props;
    const { showSearch } = this.state;

    if (renderMode === constants.renderModes.PREVIEW || renderMode === constants.renderModes.EDIT) {
      this.calculateSearchWidth();
    }

    if (renderMode === constants.renderModes.EDIT && showSearch === true) {
      this.searchClose();
    }
  }

  prepareForIE11() {
    this.isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
    this.viewTablet = document.getElementById('view-tablet');
  }

  handleResize() {
    const { showSearch } = this.state;
    const { isMobile, renderMode } = this.props;
    if (
      this.mounted === false ||
      typeof document === 'undefined' ||
      renderMode !== constants.renderModes.PREVIEW ||
      !showSearch ||
      this.isIE11 === false
    ) {
      return;
    }
    const targetContainer = document.getElementById(
      `${this._uniqueId}-container`
    );
    if (targetContainer && isMobile) {
      this.calculateSearchPos();
      targetContainer.style.left = this.ieSearchPos[0] + 'px';
      targetContainer.style.top = this.ieSearchPos[1] + 'px';
    }
  }

  calculateSearchWidth() {
    const { widthId, isMobile, minWidth, pipeWidth, searchFormLocation, membershipOn } = this.props;
    if (isMobile) return;

    const searchWidthRef = document.getElementById(widthId);
    if (searchWidthRef) {
      const width = searchWidthRef.offsetWidth;

      if (width) {
        if (width < minWidth) {
          this.searchWidth = minWidth;
        } else {
          // Compensate for pipe width in some situations bc header
          // layout doesn't allow us to wrap both navigation & pipe
          // in a Base component (Messes up More dropdown calc)
          const widthAdj = pipeWidth ? 35 : 0;
          this.searchWidth = width + widthAdj;
        }
      }

      this.searchWidth += SEARCH_INPUT_LEFT_PADDING; // Adjustment for search icon width
    }

    if (searchFormLocation === DESKTOP_NAV_COVER) {
      this.searchWidth += (membershipOn ? 150 : 100);
    }
  }

  calculateScreenWidth() {
    const w = window;
    const d = document;
    const documentElement = d.documentElement;
    const body = d.getElementsByTagName('body')[0];
    this.screenWidth =
      w.innerWidth || documentElement.clientWidth || body.clientWidth;
  }

  calculateNavWidth() {
    const { isMobile, navBarId } = this.props;
    if (isMobile) return;

    const navContainer = document.getElementById(navBarId);
    if (navContainer) {
      this.navWidth = navContainer.offsetWidth;
    }
  }

  calculateSearchPos() {
    const { renderMode, isMobile } = this.props;
    const isPreviewAndIE11 = renderMode === constants.renderModes.PREVIEW && this.isIE11;
    let dTop, dLeft, vWidth, vHeight;
    if (isPreviewAndIE11 && isMobile) {
      const viewport = document.getElementsByClassName('viewport')[0];
      if (viewport) {
        const viewportRect = viewport.getBoundingClientRect();
        dLeft = viewportRect.left;
        dTop = viewportRect.top;
        vWidth = viewport.offsetWidth;
        vHeight = viewport.offsetHeight;
      }
    }
    this.ieSearchPos = [dLeft, dTop, vWidth, vHeight];
  }

  handleFormSubmit(e) {
    e.preventDefault();
    const { isShopPage, inNavigationDrawer } = this.props;

    // Remove focus from input field
    document.activeElement &&
      document.activeElement.blur &&
      document.activeElement.blur();

    const useBestMatch = OlsConfigStore.getState().use_best_match;
    const searchAnchor = document.getElementById(this._anchorId);
    if (isShopPage) {
      const keywords = this.state.searchQuery;
      if (keywords) {
        const searchParams = { keywords };
        if (useBestMatch) searchParams.sortOption = 'descend_by_match';
        RouteHandler.navigate(ShopViewConstants.SEARCH_RESULTS, searchParams);
        ScrollWidgetActions.scrollShopWidget();
      }
    } else if (searchAnchor && searchAnchor.click) {
      searchAnchor.click();
    }
    if (inNavigationDrawer) {
      const closeDropdownAnchor = document.getElementById(this._closeId);
      if (closeDropdownAnchor && closeDropdownAnchor.click) {
        closeDropdownAnchor.click();
      }
    }
    this.searchClose();
    return false;
  }

  searchOpen() {
    setTimeout(() => {
      // Wrapped in setTimeout so that it waits until input is not disabled
      const targetField = document.getElementById(`${this._uniqueId}-input`);
      targetField.focus();
    }, 0);

    this.setState({ showSearch: true }, () => {
      this.handleDomSideEffect(this.state.showSearch);
    });

    this.calculateSearchPos();

    document.ontouchmove = function (e) {
      e.preventDefault();
    };
  }

  searchClose() {
    this.setState({
      showSearch: false,
      searchQuery: ''
    }, () => {
      this.handleDomSideEffect(this.state.showSearch);
    });

    document.ontouchmove = function () {
      return true;
    };
  }

  handleDomSideEffect(showSearch) {
    const { renderMode } = this.props;
    const viewportContainer = getMobileRenderContainer(renderMode);
    viewportContainer.style['overflow-y'] = showSearch ? 'hidden' : 'scroll';
  }

  renderSearchForm() {
    const {
      isShopPage,
      shopPageId,
      shopRoute,
      renderMode,
      searchPosition,
      searchBgStyle,
      isMobile,
      inNavigationDrawer,
      iconSizeMobile,
      iconSizeDesktop,
      domainName,
      pageRoute,
      staticContent,
      iconSection,
      section,
      keepOpen,
      searchWidthAdj,
      searchFormLocation,
      sidebarWidth,
      searchCategoryOverrides,
      iconStyles,
      formContainerId,
      onHomepage,
      hasNavBackground
    } = this.props;
    const {
      showSearch,
      searchQuery
    } = this.state;

    return (
      <SearchForm
        isShopPage={ isShopPage }
        shopPageId={ shopPageId }
        shopRoute={ shopRoute }
        renderMode={ renderMode }
        searchPosition={ searchPosition }
        searchBgStyle={ searchBgStyle }
        isMobile={ isMobile }
        inNavigationDrawer={ inNavigationDrawer }
        iconSizeMobile={ iconSizeMobile }
        iconSizeDesktop={ iconSizeDesktop }
        domainName={ domainName }
        pageRoute={ pageRoute }
        ieSearchPos={ this.ieSearchPos }
        isIE11={ this.isIE11 }
        showSearch={ showSearch || keepOpen }
        navWidth={ this.navWidth }
        screenWidth={ this.screenWidth }
        searchWidth={ this.searchWidth + searchWidthAdj }
        searchQuery={ searchQuery }
        uniqueId={ this._uniqueId }
        searchClose={ this.searchClose }
        staticContent={ staticContent }
        handleFormSubmit={ this.handleFormSubmit }
        searchInputOnChange={ newValue => this.setState(newValue) }
        iconSection={ iconSection }
        section={ section }
        searchOpen={ this.searchOpen }
        anchorId={ this._anchorId }
        closeId={ this._closeId }
        searchFormLocation={ searchFormLocation }
        sidebarWidth={ sidebarWidth }
        searchCategoryOverrides={ searchCategoryOverrides }
        iconStyles={ iconStyles }
        formContainerId={ formContainerId }
        onHomepage={ onHomepage }
        hasNavBackground={ hasNavBackground }
        useBestMatch={ OlsConfigStore.getState().use_best_match }
      />
    );
  }

  render() {
    return (
      <UX2.Element.Block
        style={{ ...alignCenter, fontSize: 'xsmall' }}
        children={ this.renderSearchForm() }
      />
    );
  }
}

Search.propTypes = SearchPropTypes;

Search.defaultProps = {
  searchWidthAdj: 0,
  minWidth: 200,
  pipeWidth: false,
  isMobile: false,
  inNavigationDrawer: false,
  keepOpen: false,
  iconSizeMobile: MIN_TARGET,
  iconSizeDesktop: MIN_TARGET,
  searchWidth: 400,
  iconStyles: {}
};

export default Search;
